import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Underwriter, useUnderwritersQuery } from '../../graphql/types';
import { debounce, delay } from 'lodash';
import { Autocomplete, ClickAwayListener, Stack, TextField } from '@mui/material';
import AssetLogo from '../AssetLogo';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { Box } from '@mui/system';
import { Values as MCAValues } from '../merchants/AddMerchantCashAdvance';
import { Values as OfferValues } from '../offers/AddRehabOffers';
import McaUnderwriterWidget from 'sections/merchants/McaUnderwriterWidget';

export interface Props<FormValues extends MCAValues | OfferValues> {
  id: string;
  disabled: boolean;
  type?: string;
  isMca?: boolean;
  formik: FormikProps<FormValues>;
}

export const FunderAutocomplete = <FormValues extends MCAValues | OfferValues>({ id, disabled, formik, type }: Props<FormValues>) => {
  const { errors, touched, values, handleBlur, setFieldValue } = formik;
  const [showInfo, setShowInfo] = useState<boolean>(disabled || !!values?.funder?.id);
  useEffect(() => {
    setShowInfo(disabled || !!values?.funder?.id);
  }, [disabled, values?.funder?.id]);
  const inputReference = useRef<HTMLInputElement>(null);
  const [lenders, setUnderwriters] = useState<readonly Underwriter[]>([]);
  const [lenderInputValue, setUnderwriterInputValue] = useState<string>('');

  const {
    loading: lendersLoading,
    data: lendersData,
    refetch: lendersRefetch
  } = useUnderwritersQuery({
    variables: {},
    fetchPolicy: 'network-only',
    skip: disabled
  });

  const lendersFetch = useMemo(
    () =>
      debounce(
        (request: { input: string }) =>
          lendersRefetch({ search: request.input }).then((rq) => setUnderwriters((rq.data.underwriters.data as Underwriter[]) || [])),
        200
      ),
    [lendersRefetch]
  );

  useEffect(() => setUnderwriters((lendersData?.underwriters.data as Underwriter[]) || []), [lendersData?.underwriters.data, lendersLoading]);

  useEffect(() => {
    if (disabled) return;
    lendersFetch({ input: lenderInputValue });
  }, [lenderInputValue, lendersFetch, disabled]);

  const renderInput = (params: any) => (
    <TextField
      {...params}
      fullWidth
      placeholder={type ? `Select ${type}` : 'Select Underwriter'}
      inputRef={inputReference}
      name={type ? type : 'funder'}
      onBlur={handleBlur}
      error={Boolean((touched.funder as FormikTouched<{ id: string }>)?.id && (errors.funder as FormikErrors<{ id: string }>)?.id)}
      helperText={Boolean((touched.funder as FormikTouched<{ id: string }>)?.id && (errors.funder as FormikErrors<{ id: string }>)?.id)}
      InputProps={{
        ...params.InputProps,
        startAdornment: values?.funder?.id && <AssetLogo logo={values?.funder?.logo} name={values?.funder?.name} size={'sm'} />
      }}
      sx={{
        '& fieldset': { border: 'none' },
        '& .MuiInputLabel-root': { padding: '14px 0 8px 16px' },
        '& .MuiOutlinedInput-root': { padding: '14px 0 8px 16px' }
      }}
    />
  );

  const renderOption = (props: any, option: Underwriter) => {
     const { key, ...rest } = props;
    return (
    <Box
      component="li"
      {...rest}
      key={key}
      sx={{
        display: 'flex',
        width: '100%',
        px: 1,
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
        {
            <McaUnderwriterWidget
                underwriter={option}
            />
        }
    </Box>
  )};

  const handleChange = (event: SyntheticEvent, newValue: Underwriter | null) => {
    setFieldValue('funder', newValue || null);
    setShowInfo(!!newValue?.id);
  };

  const handleInputChange = (event: SyntheticEvent, newInputValue: string) => {
    setUnderwriterInputValue(newInputValue);
  };

  const handleClickAway = () => {
    setShowInfo(disabled ? false : !!values?.funder?.id);
  };

  return (
    <Stack spacing={0.5} sx={{ width: '100%' }}>
      {values?.funder?.id && showInfo ? (
        <Stack
          onClick={() => {
            if (disabled) return;
            setShowInfo(false);
            delay(() => inputReference.current?.focus(), 500);
          }}
          sx={{ px: 2 }}
        >

          {
            <McaUnderwriterWidget
              underwriter={values.funder}
            />
          }
        </Stack>
      ) : (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Autocomplete
            disabled={disabled}
            id={id}
            getOptionLabel={(option: Underwriter) => `${option.name || ''}`}
            options={lenders}
            autoComplete
            value={(values.funder?.id ? values.funder as Underwriter : null)}
            isOptionEqualToValue={(option: Underwriter, value: Underwriter) =>
              !!value?.id && option.id === value.id
            }
            onChange={handleChange}
            onInputChange={handleInputChange}
            renderInput={renderInput}
            renderOption={renderOption}
          />
        </ClickAwayListener>
      )}
      {(touched.funder as FormikTouched<{ id: string }>)?.id && (errors.funder as FormikErrors<{ id: string }>)?.id && (
        <div
          style={{
            color: '#d32f2f',
            fontSize: '0.75rem',
            marginLeft: '14px',
            marginTop: showInfo ? '8px' : '-4px'
          }}
        >
          {(errors.funder as FormikErrors<{ id: string }>).id}
        </div>
      )}
    </Stack>
  );
};

export default FunderAutocomplete;
